import { NgClass } from '@angular/common';
import { Component, OnInit, Signal, signal, ViewChild, WritableSignal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MessageService } from 'primeng/api';
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import { finalize } from 'rxjs';
import { AddressFormComponent } from '../../../components/forms/address-form/address-form.component';
import { RegisterFormComponent } from '../../../components/forms/register-form/register-form.component';
import { UserBadgeComponent } from '../../../components/user-badge/user-badge.component';
import { AddressService } from '../../../services/address/address.service';
import { Address } from '../../../shared/models/address.model';
import { Pagination } from '../../../shared/models/pagination.model';
import { type User } from '../../../shared/models/user.model';
import { AccessService } from './../../../services/access/access.service';
import { UserService } from './../../../services/user/user.service';
import { CardComponent } from '../../../shared/components/card/card.component';

@Component({
  selector: 'app-profile',
  imports: [
    CardComponent,
    AddressFormComponent,
    RegisterFormComponent,
    ToggleSwitchModule,
    FormsModule,
    NgClass,
    UserBadgeComponent
  ],
  templateUrl: './profile.component.html',
  styleUrl: './profile.component.scss'
})
export class ProfileComponent implements OnInit {
  @ViewChild(RegisterFormComponent) registerForm!: RegisterFormComponent;

  readonly isUserEditable = signal(false);
  readonly isUserFormLoading = signal(false);

  readonly isAddressEditable = signal(false);
  readonly isAddressFormLoading = signal(false);
  readonly isAddressCardLoading = signal(false);

  readonly user: Signal<User | null>;
  readonly address: WritableSignal<Address | null> = signal(null);

  constructor(private readonly accessService: AccessService,
    private readonly messageService: MessageService,
    private readonly userService: UserService,
    private readonly addressService: AddressService) {
    this.user = this.accessService.userInfo;
  }

  ngOnInit(): void {
    this.isAddressCardLoading.set(true);
    this.addressService.paginateAddressByUserId(this.user()!.id!).pipe(
      finalize(() => this.isAddressCardLoading.set(false))
    ).subscribe({
      next: (addresses: Pagination<Address>) => {
        if (addresses.count > 0) {
          this.address.set(addresses.rows[0]);
        } else {
          this.address.set(null);
        }
      },
      error: () => {
        this.messageService.add({
          severity: 'error',
          summary: 'Erro',
          detail: 'Falha ao recuperar endereço!',
          life: 4000
        });
      }
    })
  }

  updateUser(fields: any): void {
    const { name, lastName, birthdate, phone, email, password } = fields;
    const newUser: User = { id: this.user()?.id, name, lastName, birthdate, phone, email };

    if (password) {
      newUser.password = password;
    }

    this.userService.updateUser(newUser)
      .pipe(
        finalize(() => this.isUserFormLoading.set(false))
      ).subscribe({
        next: (updatedUser: User) => {
          this.messageService.add({
            severity: 'success',
            summary: 'Sucesso',
            detail: 'Seus dados foram atualizados!',
            life: 4000
          });

          this.accessService.userInfo = updatedUser;
          this.isUserEditable.set(false);
          this.registerForm.isChangePasswordEnabled.set(false);
        },
        error: () => {
          this.messageService.add({
            severity: 'error',
            summary: 'Erro',
            detail: 'Falha ao atualizar usuário!',
            life: 4000
          });
        }
      });
  }

  createOrUpdateAddress(fields: any): void {
    const isUpdate = this.address() !== null;

    let serviceAction;
    if (isUpdate) {
      fields.id = this.address()!.id;
      serviceAction = this.addressService.updateAddress(fields);
    } else {
      serviceAction = this.addressService.createAddress(fields);
    }

    serviceAction.pipe(
      finalize(() => this.isAddressFormLoading.set(false))
    ).subscribe({
      next: (address: Address) => {
        this.messageService.add({
          severity: 'success',
          summary: 'Atualização bem sucedida',
          detail: 'Seu endereço foi atualizado com sucesso!',
          life: 4000
        });

        this.address.set(address);
        this.isAddressEditable.set(false);
      },
      error: () => {
        this.messageService.add({
          severity: 'error',
          summary: 'Erro',
          detail: 'Falha ao atualizar endereço!',
          life: 4000
        });
      }
    });
  }
}
